<!-- frontend/src/views/employee/Profile.vue -->
<template>
  <div class="ep-page max-w-4xl">

    <!-- ── Loading ────────────────────────────────────────── -->
    <div v-if="loading" class="ep-card p-10 text-center">
      <div class="ep-spinner mb-4"></div>
      <p class="text-sm text-slate-500">{{ t('empProfile.loading') }}</p>
    </div>

    <!-- ── Error ──────────────────────────────────────────── -->
    <div v-else-if="error"
         class="rounded-2xl p-5 flex items-start gap-3"
         style="background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25)">
      <svg class="h-5 w-5 text-red-400 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
      </svg>
      <div>
        <h3 class="text-sm font-semibold text-red-300">{{ t('empProfile.errorLoading') }}</h3>
        <p class="mt-1 text-sm text-red-300/80">{{ error }}</p>
      </div>
    </div>

    <!-- ── Profile ────────────────────────────────────────── -->
    <template v-else>
      <div class="ep-card overflow-hidden">
        <!-- Hero -->
        <div class="px-6 py-8 relative overflow-hidden"
             style="background: linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(124,58,237,0.16) 100%); border-bottom: 1px solid rgba(129,140,248,0.25)">
          <div class="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
               style="background: radial-gradient(circle, rgba(129,140,248,0.18) 0%, transparent 70%)"></div>
          <div class="relative flex items-center gap-5">
            <div class="w-20 h-20 rounded-2xl flex items-center justify-center shrink-0"
                 style="background: rgba(99,102,241,0.25); border: 1px solid rgba(129,140,248,0.4); box-shadow: 0 0 24px rgba(99,102,241,0.3)">
              <User class="w-10 h-10 text-indigo-200" />
            </div>
            <div class="min-w-0">
              <h1 class="text-xl sm:text-2xl font-bold text-white truncate">
                {{ profile.personalInfo?.name || 'Loading…' }}
              </h1>
              <p class="text-sm text-indigo-200/80 mt-0.5">
                {{ profile.employeeCode || 'N/A' }} · {{ profile.workInfo?.department || 'N/A' }}
              </p>
              <p class="text-xs text-indigo-200/60 mt-0.5">{{ profile.workInfo?.position || 'N/A' }}</p>
            </div>
          </div>
        </div>

        <div class="p-6 space-y-8">
          <!-- ── Personal Information ── -->
          <section>
            <h3 class="ep-section-title mb-4">
              <span class="ep-stat-icon ep-stat-indigo !w-8 !h-8 !rounded-lg"><ContactIcon class="w-4 h-4" /></span>
              {{ t('empProfile.personalInfo') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="ep-label">{{ t('empProfile.name') }}</label>
                <input v-model="editableProfile.name" class="ep-input" maxlength="255" :disabled="!isEditing" />
              </div>
              <div>
                <label class="ep-label">{{ t('empProfile.email') }}</label>
                <input :value="profile.personalInfo?.email || ''" class="ep-input" disabled />
              </div>
              <div>
                <label class="ep-label">{{ t('empProfile.phone') }}</label>
                <input v-model="editableProfile.phone" class="ep-input" maxlength="50"
                       :placeholder="t('empProfile.phonePlaceholder')" :disabled="!isEditing" />
              </div>
              <div class="md:col-span-2">
                <label class="ep-label">{{ t('empProfile.address') }}</label>
                <textarea v-model="editableProfile.address" rows="2" class="ep-textarea" maxlength="2000"
                          :disabled="!isEditing"></textarea>
              </div>
              <div>
                <label class="ep-label">{{ t('empProfile.emergencyName') }}</label>
                <input v-model="editableProfile.emergencyContactName" class="ep-input" maxlength="255" :disabled="!isEditing" />
              </div>
              <div>
                <label class="ep-label">{{ t('empProfile.emergencyPhone') }}</label>
                <input v-model="editableProfile.emergencyContactPhone" class="ep-input" maxlength="50"
                       :placeholder="t('empProfile.phonePlaceholder')" :disabled="!isEditing" />
              </div>
            </div>
          </section>

          <!-- ── Account & Security ── -->
          <section>
            <h3 class="ep-section-title mb-4">
              <span class="ep-stat-icon ep-stat-emerald !w-8 !h-8 !rounded-lg"><Shield class="w-4 h-4" /></span>
              {{ t('empProfile.accountSecurity') }}
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <p class="ep-label">{{ t('empProfile.joinDate') }}</p>
                <p class="text-sm text-slate-200">{{ formatDate(profile.accountInfo?.joinDate) }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.lastLogin') }}</p>
                <p class="text-sm text-slate-200">{{ formatDate(profile.accountInfo?.lastLogin) || t('empProfile.never') }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.lastUpdated') }}</p>
                <p class="text-sm text-slate-200">{{ formatDate(profile.accountInfo?.lastUpdated) }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.accountStatus') }}</p>
                <span :class="['ep-badge', profile.accountInfo?.isActive ? 'ep-badge-success' : 'ep-badge-danger']">
                  <span class="ep-dot"></span>{{ profile.accountInfo?.isActive ? t('empProfile.active') : t('empProfile.inactive') }}
                </span>
              </div>
            </div>

            <!-- Security settings -->
            <div class="mt-5 space-y-3">
              <div class="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                   style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
                <div>
                  <h4 class="text-sm font-semibold text-slate-200">{{ t('empProfile.password') }}</h4>
                  <p class="text-xs text-slate-500 mt-0.5">{{ t('empProfile.lastChanged') }} {{ lastPasswordChange }}</p>
                </div>
                <button @click="showChangePassword = true" class="ep-btn ep-btn-secondary ep-btn-sm self-start sm:self-auto">
                  {{ t('empProfile.changePassword') }}
                </button>
              </div>

              <div class="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 opacity-70"
                   style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
                <div>
                  <h4 class="text-sm font-semibold text-slate-200">{{ t('empProfile.twoFactor') }}</h4>
                  <p class="text-xs text-slate-500 mt-0.5">{{ t('empProfile.twoFactorDesc') }}</p>
                </div>
                <div class="flex items-center gap-2.5 self-start sm:self-auto">
                  <span class="ep-badge ep-badge-neutral">{{ t('empProfile.comingSoon') }}</span>
                  <button disabled class="ep-btn ep-btn-sm ep-btn-secondary opacity-50 cursor-not-allowed"
                          :title="t('empProfile.twoFactorNotice')">
                    {{ t('empProfile.enable2fa') }}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- ── Work Information ── -->
          <section>
            <h3 class="ep-section-title mb-4">
              <span class="ep-stat-icon ep-stat-violet !w-8 !h-8 !rounded-lg"><Calendar class="w-4 h-4" /></span>
              {{ t('empProfile.workInfo') }}
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <p class="ep-label">{{ t('empProfile.department') }}</p>
                <p class="text-sm text-slate-200">{{ profile.workInfo?.department || 'N/A' }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.manager') }}</p>
                <p class="text-sm text-slate-200">{{ profile.workInfo?.manager || 'N/A' }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.position') }}</p>
                <p class="text-sm text-slate-200">{{ profile.workInfo?.position || 'N/A' }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.status') }}</p>
                <span :class="['ep-badge', profile.workInfo?.status === 'active' ? 'ep-badge-success' : 'ep-badge-danger']">
                  <span class="ep-dot"></span>
                  {{ (profile.workInfo?.status || 'unknown').charAt(0).toUpperCase() + (profile.workInfo?.status || 'unknown').slice(1) }}
                </span>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.hireDate') }}</p>
                <p class="text-sm text-slate-200">{{ formatDate(profile.workInfo?.hireDate) }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empProfile.canWorkField') }}</p>
                <span :class="['ep-badge', profile.workInfo?.canWorkField ? 'ep-badge-success' : 'ep-badge-neutral']">
                  {{ profile.workInfo?.canWorkField ? t('empProfile.yes') : t('empProfile.no') }}
                </span>
              </div>
            </div>
          </section>

          <!-- ── Leave Information ── -->
          <section>
            <h3 class="ep-section-title mb-4">
              <span class="ep-stat-icon ep-stat-amber !w-8 !h-8 !rounded-lg"><Calendar class="w-4 h-4" /></span>
              {{ t('empProfile.leaveInfo') }}
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                v-for="balance in leaveBalances"
                :key="balance.leave_type_id"
                class="rounded-xl p-4"
                style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)"
              >
                <h4 class="text-sm font-semibold text-slate-200">{{ balance.leave_type }}</h4>
                <div class="mt-2.5">
                  <div class="flex justify-between text-xs text-slate-500 mb-1.5">
                    <span>{{ t('empProfile.used') }}</span>
                    <span class="tabular-nums">{{ balance.used_days }}/{{ balance.total_days }}</span>
                  </div>
                  <div class="ep-progress">
                    <div class="ep-progress-bar"
                         :style="{ width: Math.min(100, balance.total_days > 0 ? (balance.used_days / balance.total_days) * 100 : 0) + '%' }"></div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5">{{ balance.remaining_days }} {{ t('empProfile.daysRemaining') }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- ── Actions ── -->
          <div v-if="saveError" class="rounded-xl px-4 py-3 text-sm text-red-300"
               style="background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25)">
            {{ saveError }}
          </div>
          <div class="flex justify-end gap-2.5 pt-5" style="border-top: 1px solid rgba(255,255,255,0.07)">
            <router-link v-if="!isEditing" to="/employee/face-enrollment" class="ep-btn ep-btn-ghost">
              Face Enrollment (Veridic trial)
            </router-link>
            <button v-if="!isEditing" @click="isEditing = true" class="ep-btn ep-btn-secondary">
              <Edit class="w-4 h-4" /> {{ t('empProfile.editProfile') }}
            </button>
            <template v-else>
              <button @click="cancelEdit" :disabled="saving" class="ep-btn ep-btn-ghost">{{ t('common.cancel') }}</button>
              <button @click="saveProfile" :disabled="saving" class="ep-btn ep-btn-primary">
                <Save class="w-4 h-4" /> {{ saving ? t('empProfile.saving') : t('empProfile.saveChanges') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Modals ─────────────────────────────────────────── -->
    <ChangePasswordModal
      v-if="showChangePassword"
      @close="showChangePassword = false"
      @success="handlePasswordChanged"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import {
  User,
  ContactIcon,
  Shield,
  Calendar,
  Edit,
  Save,
} from 'lucide-vue-next';
import ChangePasswordModal from '@/components/ui/modals/ChangePasswordModal.vue';
import profileService from '@/services/profile.js';

const profile = ref({
  personalInfo: {},
  workInfo: {},
  accountInfo: {}
});
const editableProfile = ref({
  name: '',
  phone: '',
  address: '',
  emergencyContactName: '',
  emergencyContactPhone: ''
});
const leaveBalances = ref([]);
const isEditing = ref(false);
const showChangePassword = ref(false);
const originalProfile = ref({});
const loading = ref(true);
const saving = ref(false);
const error = ref(null);
const saveError = ref(null);

const lastPasswordChange = computed(() => {
  if (profile.value.last_password_change) {
    return new Date(profile.value.last_password_change).toLocaleDateString();
  }
  return 'Never';
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const fetchEmployeeProfile = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await profileService.getProfile();
    if (response.success) {
      profile.value = response.data.profile;
      editableProfile.value = {
        name: response.data.profile.personalInfo?.name || '',
        phone: response.data.profile.personalInfo?.phone || '',
        address: response.data.profile.personalInfo?.address || '',
        emergencyContactName: response.data.profile.personalInfo?.emergencyContactName || '',
        emergencyContactPhone: response.data.profile.personalInfo?.emergencyContactPhone || '',
      };
      originalProfile.value = { ...editableProfile.value };
    } else {
      error.value = response.error || 'Failed to fetch profile';
    }
  } catch (err) {
    console.error('Failed to fetch profile:', err);
    error.value = 'Failed to fetch profile data';
  } finally {
    loading.value = false;
  }
};

const fetchLeaveBalances = async () => {
  try {
    const response = await profileService.getLeaveBalance();
    if (response.success) {
      leaveBalances.value = (response.data?.balanceBanks || []).map(bank => ({
        leave_type_id: bank.bankId,
        leave_type: bank.name,
        used_days: bank.used,
        total_days: bank.totalAvailable,
        remaining_days: bank.remaining,
      }));
    }
  } catch (error) {
    console.error('Failed to fetch leave balances:', error);
    leaveBalances.value = [];
  }
};

const saveProfile = async () => {
  if (saving.value) return;
  try {
    saving.value = true;
    saveError.value = null;

    const response = await profileService.updatePersonalInfo({
      name: editableProfile.value.name,
      phone: editableProfile.value.phone,
      address: editableProfile.value.address,
      emergencyContactName: editableProfile.value.emergencyContactName,
      emergencyContactPhone: editableProfile.value.emergencyContactPhone,
    });

    if (response.success) {
      isEditing.value = false;
      originalProfile.value = { ...editableProfile.value };
      // Update the profile data
      profile.value.personalInfo = {
        ...profile.value.personalInfo,
        name: editableProfile.value.name,
        phone: editableProfile.value.phone,
        address: editableProfile.value.address,
        emergencyContactName: editableProfile.value.emergencyContactName,
        emergencyContactPhone: editableProfile.value.emergencyContactPhone,
      };
    } else {
      saveError.value = response.error || 'Failed to save profile';
    }
  } catch (err) {
    console.error('Failed to save profile:', err);
    saveError.value = err.response?.data?.error || 'Failed to save profile';
  } finally {
    saving.value = false;
  }
};

const cancelEdit = () => {
  editableProfile.value = { ...originalProfile.value };
  saveError.value = null;
  isEditing.value = false;
};

const handlePasswordChanged = () => {
  showChangePassword.value = false;
  // Update last password change
  profile.value.last_password_change = new Date();
};

onMounted(() => {
  fetchEmployeeProfile();
  fetchLeaveBalances();
});
</script>
